import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "./toast.css";

export type ToastTone = "info" | "success" | "warning" | "error";

export interface ToastInput {
  id?: string;
  tone?: ToastTone;
  title: string;
  description?: string;
  /** ms; 0 = sticky. Defaults: 4000 (info/success), 6000 (warning/error). */
  durationMs?: number;
}

interface ActiveToast extends Required<Omit<ToastInput, "description">> {
  description: string | undefined;
}

interface ToastContextValue {
  push: (t: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_TOASTS = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = input.id ?? `t-${++counterRef.current}`;
      const tone = input.tone ?? "info";
      const defaultDuration = tone === "error" || tone === "warning" ? 6000 : 4000;
      const next: ActiveToast = {
        id,
        tone,
        title: input.title,
        description: input.description,
        durationMs: input.durationMs ?? defaultDuration,
      };
      setToasts((current) => {
        const trimmed = current.length >= MAX_TOASTS ? current.slice(1) : current;
        return [...trimmed.filter((t) => t.id !== id), next];
      });
      return id;
    },
    [],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ActiveToast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="wp-toast-viewport" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ActiveToast;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    if (toast.durationMs === 0) return;
    const handle = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(handle);
  }, [toast.id, toast.durationMs, onDismiss]);

  const role = toast.tone === "error" ? "alert" : "status";

  return (
    <div className={`wp-toast wp-toast--${toast.tone}`} role={role} data-testid="toast">
      <div className="wp-toast__content">
        <strong className="wp-toast__title">{toast.title}</strong>
        {toast.description ? (
          <span className="wp-toast__desc">{toast.description}</span>
        ) : null}
      </div>
      <button
        type="button"
        className="wp-toast__close"
        aria-label="Закрити сповіщення"
        onClick={() => onDismiss(toast.id)}
      >
        ×
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
